import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πώς καταλαβαίνω ότι ένα τατουάζ έχει μολυνθεί;", acceptedAnswer: { "@type": "Answer", text: "Τα κύρια σημάδια είναι το κοκκίνισμα που απλώνεται και χειροτερεύει, το αυξανόμενο πρήξιμο, ο έντονος πόνος, το πύον με κακή οσμή, η υπερβολική ζέστη, ο πυρετός και οι κόκκινες γραμμές που εκτείνονται από το τατουάζ. Αυτά τα συμπτώματα χειροτερεύουν αντί να βελτιώνονται." } },
    { "@type": "Question", name: "Είναι φυσιολογικό να κοκκινίζει το τατουάζ μου;", acceptedAnswer: { "@type": "Answer", text: "Ελαφρύ κοκκίνισμα τις πρώτες μέρες είναι φυσιολογικό και υποχωρεί σταδιακά. Αν όμως το κοκκίνισμα απλώνεται, εντείνεται ή χειροτερεύει μετά τις πρώτες ημέρες, μπορεί να είναι σημάδι μόλυνσης." } },
    { "@type": "Question", name: "Τι είναι το φυσιολογικό έκκριμα και τι το πύον;", acceptedAnswer: { "@type": "Answer", text: "Το φυσιολογικό έκκριμα τις πρώτες μέρες είναι διαυγές ή ελαφρώς θολό, μερικές φορές με λίγο μελάνι. Το πύον είναι πυκνό, κίτρινο ή κιτρινοπράσινο υγρό, συχνά με δυσάρεστη οσμή, και είναι σημάδι μόλυνσης." } },
    { "@type": "Question", name: "Τι να κάνω αν νομίζω ότι το τατουάζ μου έχει μολυνθεί;", acceptedAnswer: { "@type": "Answer", text: "Επικοινώνησε με τον tattoo artist σου και περίγραψε τα συμπτώματα. Αν τα σημάδια είναι έντονα, με πύον, πυρετό, έντονο πόνο ή κόκκινες γραμμές, πήγαινε σε γιατρό άμεσα. Μην προσπαθήσεις να το αντιμετωπίσεις μόνος σου με αυτοσχέδιες λύσεις." } },
    { "@type": "Question", name: "Πόσο συχνές είναι οι μολύνσεις στα τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Οι μολύνσεις δεν είναι συχνές όταν η δουλειά γίνεται σε σωστές συνθήκες υγιεινής και το τατουάζ φροντίζεται σωστά. Ο κίνδυνος αυξάνεται με κακή υγιεινή στο στούντιο ή με κακή φροντίδα μετά." } },
    { "@type": "Question", name: "Πώς μπορώ να προλάβω μια μόλυνση;", acceptedAnswer: { "@type": "Answer", text: "Διάλεξε αξιόπιστο στούντιο με αυστηρή υγιεινή, ακολούθησε τις οδηγίες φροντίδας, κράτα την περιοχή καθαρή, μην ξύνεις τις φλούδες και απόφυγε θάλασσα και πισίνα μέχρι την πλήρη επούλωση." } },
    { "@type": "Question", name: "Μπορεί να είναι αλλεργία αντί για μόλυνση;", acceptedAnswer: { "@type": "Answer", text: "Ναι. Μερικές φορές ο κνησμός, το εξάνθημα ή οι φουσκάλες, ειδικά γύρω από ένα συγκεκριμένο χρώμα μελανιού, μπορεί να είναι αλλεργική αντίδραση. Σε κάθε περίπτωση, συμβουλέψου γιατρό για σωστή διάγνωση." } },
  ],
};

export default function TattooInfectionSignsAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί;",
      "description": "Πώς να ξεχωρίσεις τη φυσιολογική επούλωση από τη μόλυνση, ποια σημάδια χρειάζονται προσοχή και τι να κάνεις σε κάθε περίπτωση.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-06-11",
      "dateModified": "2026-06-11",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-infection-signs-athens",
      "inLanguage": "el"
    };
    inject('schema-tattoo-infection-signs-faq', faqSchema);
    inject('schema-tattoo-infection-signs-article', articleSchema);
    return () => {
      document.getElementById('schema-tattoo-infection-signs-faq')?.remove();
      document.getElementById('schema-tattoo-infection-signs-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί; | 210 Tattoo Athens</title>
        <meta name="description" content="Πώς να ξεχωρίσεις τη φυσιολογική επούλωση από τη μόλυνση, ποια σημάδια χρειάζονται προσοχή και τι να κάνεις σε κάθε περίπτωση." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-infection-signs-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-infection-signs-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-infection-signs-athens" />
        <meta property="og:title" content="Πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί; | 210 Tattoo Athens" />
        <meta property="og:description" content="Τα σημάδια μόλυνσης σε ένα τατουάζ και πώς να τα ξεχωρίσεις από τη φυσιολογική επούλωση." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-infection-signs-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-infection-signs-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Σημάδια μόλυνσης τατουάζ και τι να κάνεις." />
        <meta name="twitter:image" content={ogImage('tattoo-infection-signs-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Μόλυνση Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Πώς καταλαβαίνεις</span>
              <br />
              <span className="text-white">ότι ένα τατουάζ έχει μολυνθεί;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">11 Ιουνίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Κοκκίνισμα, πρήξιμο και ευαισθησία είναι φυσιολογικά μετά το τατουάζ. Δες πού τραβάς τη γραμμή ανάμεσα στη φυσιολογική επούλωση και σε ένα πρόβλημα.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Normal first */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πρώτα τα φυσιολογικά, <span className="text-pink-500">τι να περιμένεις κατά την επούλωση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ένα φρέσκο τατουάζ είναι μια πληγή, και το σώμα αντιδρά με συγκεκριμένους τρόπους. Τις πρώτες μέρες είναι φυσιολογικό ελαφρύ κοκκίνισμα, μια ζέστη σαν ήπιο ηλιακό έγκαυμα, ελαφρύ πρήξιμο και διαυγές έκκριμα. Μετά την πρώτη εβδομάδα ξεκινάει ξεφλούδισμα και ήπια φαγούρα.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Το κλειδί είναι ότι όλα αυτά τα φυσιολογικά συμπτώματα υποχωρούν σταδιακά με τον χρόνο. Όταν τα συμπτώματα χειροτερεύουν αντί να βελτιώνονται, τότε αρχίζεις να ανησυχείς.
            </p>
          </div>
        </section>

        {/* Signs of infection */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τα σημάδια ότι ένα τατουάζ <span className="text-pink-500">έχει μολυνθεί</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Κοκκίνισμα που απλώνεται και χειροτερεύει', desc: 'Ένα κόκκινο, διογκωμένο περίγραμμα γύρω από το τατουάζ που μεγαλώνει μετά τις πρώτες μέρες είναι λόγος ανησυχίας.' },
                { title: 'Αυξανόμενο πρήξιμο', desc: 'Αν το πρήξιμο αυξάνεται αντί να μειώνεται μετά τις πρώτες ημέρες, μπορεί να υποδηλώνει μόλυνση.' },
                { title: 'Έντονος ή αυξανόμενος πόνος', desc: 'Ο πόνος πρέπει να μειώνεται σταδιακά. Αν εντείνεται ή γίνεται σφυγμικός, χρειάζεται προσοχή.' },
                { title: 'Πύον ή κακή οσμή', desc: 'Πυκνό, κίτρινο ή κιτρινοπράσινο υγρό με δυσάρεστη οσμή είναι σχεδόν σίγουρα σημάδι μόλυνσης.' },
                { title: 'Υπερβολική ζέστη στην περιοχή', desc: 'Αν η περιοχή είναι αισθητά ζεστή και αυτό παραμένει ή εντείνεται, μπορεί να είναι σημάδι φλεγμονής.' },
                { title: 'Πυρετός και γενική αδιαθεσία', desc: 'Πυρετός, ρίγη και κούραση σημαίνουν ότι κάτι σοβαρό συμβαίνει και χρειάζεσαι ιατρική βοήθεια άμεσα.' },
                { title: 'Φουσκάλες, εξανθήματα ή ανοιχτές πληγές', desc: 'Δεν είναι φυσιολογικά. Μπορεί να υποδηλώνουν μόλυνση ή αλλεργική αντίδραση.' },
                { title: 'Κόκκινες γραμμές που εκτείνονται από το τατουάζ', desc: 'Ιδιαίτερα σοβαρό σημάδι, μπορεί να δείχνει εξάπλωση μέσω του λεμφικού συστήματος. Απαιτεί άμεση ιατρική φροντίδα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Infection vs allergy */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μόλυνση ή <span className="text-pink-500">αλλεργική αντίδραση;</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Μερικές φορές αυτό που μοιάζει με μόλυνση μπορεί να είναι αλλεργική αντίδραση στο μελάνι, συχνά σχετιζόμενη με συγκεκριμένα χρώματα. Μπορεί να εμφανιστεί ως κνησμός, εξάνθημα, φουσκάλες ή διόγκωση σε σημεία με συγκεκριμένο χρώμα, και σε αντίθεση με τη μόλυνση μπορεί να εμφανιστεί ακόμα και αρκετό καιρό μετά. Σε κάθε περίπτωση, η σωστή δράση είναι να συμβουλευτείς γιατρό.
            </p>
          </div>
        </section>

        {/* What to do */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να κάνεις αν <span className="text-pink-500">υποψιάζεσαι μόλυνση</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Μην αυτοσχεδιάζεις', desc: 'Μην βάζεις τυχαία προϊόντα ή αλοιφές χωρίς καθοδήγηση, και μην σταματήσεις να καθαρίζεις την περιοχή.' },
                { title: 'Επικοινώνησε με τον tattoo artist σου', desc: 'Περίγραψε τι παρατηρείς. Ένας έμπειρος καλλιτέχνης μπορεί να σε βοηθήσει να καταλάβεις αν είναι φυσιολογικό ή πρόβλημα.' },
                { title: 'Πήγαινε σε γιατρό άμεσα αν τα σημάδια είναι έντονα', desc: 'Πύον, πυρετός, έντονος πόνος ή κόκκινες γραμμές χρειάζονται άμεση ιατρική φροντίδα.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Prevention */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να προλάβεις <span className="text-pink-500">τη μόλυνση</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Διάλεξε αξιόπιστο στούντιο με αυστηρή υγιεινή',
                'Ακολούθησε πιστά τις οδηγίες φροντίδας',
                'Κράτα την περιοχή καθαρή με ήπιο σαπούνι',
                'Πλένε τα χέρια σου πριν αγγίξεις το τατουάζ',
                'Μην ξύνεις και μην τραβάς τις φλούδες',
                'Απόφυγε θάλασσα, πισίνα και παρατεταμένο μούλιασμα',
                'Ενυδάτωσε σωστά, με λεπτή στρώση',
                'Φόρα καθαρά, άνετα ρούχα',
              ].map((text, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-5 border border-pink-600/10 flex gap-3 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <p className="text-gray-300 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Studio choice */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Η σημασία της επιλογής <span className="text-pink-500">σωστού στούντιο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Ένα σοβαρό tattoo studio τηρεί αυστηρά πρωτόκολλα υγιεινής: στείρες βελόνες μιας χρήσης, καθαρός και απολυμασμένος εξοπλισμός, γάντια και καθαρό περιβάλλον εργασίας. Όταν κάνεις τατουάζ σε ένα τέτοιο περιβάλλον, ο κίνδυνος μόλυνσης από την ίδια τη διαδικασία είναι ελάχιστος.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-8">Συχνές <span className="text-pink-500">Ερωτήσεις</span></h2>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θες σιγουριά για <span className="text-pink-500">την ασφάλειά σου;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στο 210 Tattoo Athens οι καλλιτέχνες δουλεύουν με αυστηρά πρωτόκολλα υγιεινής, στείρο εξοπλισμό και σαφείς οδηγίες φροντίδας.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM στο 210 Tattoo Athens
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-healing-time-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Όλα τα στάδια επούλωσης και πώς να φροντίσεις σωστά το νέο σου τατουάζ.</p>
              </Link>
              <Link to="/blog/tattoo-aftercare-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Φροντίδα Τατουάζ Μετά το Studio</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρεις οδηγίες aftercare για γρήγορη επούλωση.</p>
              </Link>
              <Link to="/blog/pos-katalabaineis-tatouaz-molynsi-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς Καταλαβαίνεις Μόλυνση σε Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Φυσιολογική επούλωση ή μόλυνση, τα σημάδια και τι να κάνεις.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
